import "./schedule-call-modal";
import "./footer";
import "./header";
import Swiper from "swiper";
import { Mousewheel, Navigation } from "swiper/modules";
import "swiper/css";

const header = document.getElementById("header");
const reveals = document.querySelectorAll(".dex-reveal");
const counters = document.querySelectorAll("[data-counter]");
const switchWord = document.getElementById("switchWord");
const switchWords = ["Secure", "Extend", "Create"];
const switchStepMs = 2500;
const switchTransitionMs = 500;
let wordIdx = 0;

let revealObserver: IntersectionObserver;

const animateCounters = (scope: NodeListOf<Element> | NodeListOf<Element>) => {
  const list = scope && scope.length ? scope : counters;
  list.forEach((el) => {
    const htmlEl = el as HTMLElement;
    if (htmlEl.dataset.done) return;
    htmlEl.dataset.done = "1";
    const target = Number(htmlEl.dataset.counter);
    const start = performance.now();
    const dur = 1200;
    const step = (now: number) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      const suf =
        htmlEl.dataset.suffix !== undefined ? htmlEl.dataset.suffix : "%";
      htmlEl.textContent = Math.floor(eased * target) + suf;
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  });
};

const activateReveal = (el: Element) => {
  if (el.classList.contains("in")) return;
  el.classList.add("in");
  try {
    revealObserver.unobserve(el);
  } catch {}
  const local = el.querySelectorAll("[data-counter]");
  if (local.length) animateCounters(local);
};

revealObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) activateReveal(entry.target);
    });
  },
  { threshold: 0, rootMargin: "0px 0px 100px 0px" }
);
reveals.forEach((el) => revealObserver.observe(el));

const syncRevealsFromRect = () => {
  document.querySelectorAll(".dex-reveal:not(.in)").forEach((el) => {
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const pad = 80;
    if (r.top < vh + pad && r.bottom > -pad) activateReveal(el);
  });
};

let revealScrollRaf: number | null = null;
const onRevealScroll = () => {
  if (revealScrollRaf !== null) return;
  revealScrollRaf = requestAnimationFrame(() => {
    revealScrollRaf = null;
    syncRevealsFromRect();
  });
};

window.addEventListener("scroll", onRevealScroll, { passive: true });
window.addEventListener("resize", onRevealScroll, { passive: true });
requestAnimationFrame(() => {
  requestAnimationFrame(syncRevealsFromRect);
});

const restartSafariSensitiveAnimations = () => {
  const animated = document.querySelectorAll<HTMLElement>(
    ".dex-hero-marquee-inner, .dex-why-skills-track, .dex-logos-track"
  );
  if (!animated.length) return;
  animated.forEach((el) => {
    el.style.animation = "none";
    void el.offsetWidth;
    el.style.removeProperty("animation");
    el.style.removeProperty("animation-play-state");
  });
};

let resumeAnimationsRaf: number | null = null;
const scheduleAnimationResume = () => {
  if (resumeAnimationsRaf !== null) return;
  resumeAnimationsRaf = requestAnimationFrame(() => {
    resumeAnimationsRaf = null;
    restartSafariSensitiveAnimations();
    syncRevealsFromRect();
  });
};

document.addEventListener("visibilitychange", () => {
  if (!document.hidden) scheduleAnimationResume();
});
/* Only restart marquees when restored from the bfcache. On the initial load
   `pageshow` fires too, and restarting there resets the marquee a frame after
   it starts, which reads as a visible jerk. */
window.addEventListener("pageshow", (e: PageTransitionEvent) => {
  if (e.persisted) scheduleAnimationResume();
});
window.addEventListener("focus", scheduleAnimationResume);

{
  if (switchWord) {
    const sw = switchWord as HTMLElement;
    /* Hide the word until the vertical track is built and positioned, so the
       un-laid-out / mis-measured first frame (ghost or blank word) is never
       shown. Revealed after applySwitchLayout() below. */
    sw.style.visibility = "hidden";
    const measureSwitchWidth = () => {
      const measure = sw.cloneNode(false) as HTMLElement;
      measure.removeAttribute("id");
      measure.style.position = "absolute";
      measure.style.visibility = "hidden";
      measure.style.pointerEvents = "none";
      measure.style.whiteSpace = "nowrap";
      measure.style.width = "auto";
      measure.style.maxWidth = "none";
      measure.style.display = "inline-block";
      /* The class carries the 0.4em clip-cushion padding; strip it so the
         measurement stays "text width only". */
      measure.style.padding = "0";
      sw.parentElement?.appendChild(measure);

      let maxW = 0;
      switchWords.forEach((w) => {
        measure.textContent = w;
        const wPx = measure.getBoundingClientRect().width;
        maxW = Math.max(maxW, wPx);
      });
      measure.remove();

      /* The visible window is exactly one pixel wider than the longest word,
         so the widest item never sits flush against the clip edge. */
      return Math.ceil(maxW) + 1;
    };

    let boxW = measureSwitchWidth();
    const track = document.createElement("span");
    track.className = "dex-switch-word__track";
    /* Track top-to-bottom: Build, Boost, Scale, then a trailing Build duplicate
       for a seamless loop. The visible cycle decrements the slot (3 -> 0), which
       slides each new word DOWN into view (the original, intended direction).
       Item 0 is also "Build", so the track's default translate3d(0,0,0) already
       shows a correct "Build" - the initial slot-3 positioning is deferred to a
       rAF (see below) so it never runs with a step height measured before the
       stylesheet/font applied, which was the intermittent "crooked Scale". */
    const downwardSwitchWords = [
      switchWords[0],
      ...switchWords.slice(1).reverse(),
      switchWords[0],
    ];
    let slotIdx = downwardSwitchWords.length - 1;

    downwardSwitchWords.forEach((word) => {
      const item = document.createElement("span");
      item.className = "dex-switch-word__item";
      item.textContent = word;
      track.appendChild(item);
    });

    sw.textContent = "";
    sw.setAttribute("aria-live", "polite");
    sw.appendChild(track);

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );

    const getStepHeight = () =>
      track.firstElementChild?.getBoundingClientRect().height ||
      sw.getBoundingClientRect().height ||
      64;

    const setTrackPosition = (animate = true) => {
      if (!animate) track.style.transition = "none";
      track.style.transform = `translate3d(0, -${slotIdx * getStepHeight()}px, 0)`;
      if (!animate) {
        void track.offsetHeight;
        track.style.removeProperty("transition");
      }
    };

    /* Until Manrope is CONFIRMED loaded the measurement runs in a fallback
       font, and no fallback matches its widths exactly on every platform.
       A narrow result + overflow clipping cut the word into the tail on
       first load. fonts.check()/fonts.ready are not trusted here - iOS
       Safari can report both prematurely, which is exactly the case where
       the cushion is needed most. So: cushion stays on until the load()
       promise for the latin face actually resolves. If the font never
       loads, the fallback IS the rendered font, its measurement is correct,
       and the cushion is merely a slightly wider gap. */
    let manropeConfirmed = false;

    const applySwitchLayout = () => {
      boxW = measureSwitchWidth();
      /* 12% covers the worst observed fallback deficit (SF Pro at -11.5%
         vs Manrope 600); the CSS clip-cushion padding backstops the rest. */
      if (!manropeConfirmed) boxW = Math.ceil(boxW * 1.12);
      sw.style.width = Math.ceil(boxW) + "px";
      sw.style.display = "inline-block";
      sw.style.whiteSpace = "nowrap";
      sw.style.maxWidth = "";
      setTrackPosition(false);
    };
    /* Reveal immediately: the track's default translate3d(0,0,0) shows item 0
       ("Build") correctly, with no dependency on a measured step height. Then
       defer the real slot-3 positioning + width to a rAF, by which point the
       stylesheet and layout have applied, so the step is reliable. This is what
       stops the intermittent "crooked, offset Scale" on first paint while
       keeping the original top-to-bottom slide direction. */
    sw.style.visibility = "";
    requestAnimationFrame(applySwitchLayout);
    window.addEventListener("resize", applySwitchLayout, { passive: true });
    /* The rAF above can still land before the page stylesheet has applied, in
       which case the words are measured at the pre-CSS font size and the box is
       locked far too narrow (observed: 102px against a 156px "Boost", clipping
       it) until something else triggers a resize. `load` fires only once every
       stylesheet has been applied, so this re-measure is the one guaranteed to
       run against the real heading size. */
    window.addEventListener("load", applySwitchLayout);

    const confirmManrope = () => {
      if (manropeConfirmed) return;
      manropeConfirmed = true;
      applySwitchLayout();
    };
    try {
      /* load() both kicks the fetch off immediately (instead of waiting for
         layout to request it) and resolves only on real availability. */
      document.fonts
        ?.load("600 16px 'Manrope Variable'", "CreateExtendSecure")
        .then((faces) => {
          if (faces.length > 0) confirmManrope();
        })
        .catch(() => {});
    } catch {
      /* FontFaceSet missing: nothing to wait for, cushion simply stays. */
    }
    /* Backstop for engines where the load() promise misbehaves: after any
       batch of font loads finishes, re-verify with check(). Here a true from
       check() is trustworthy because loading genuinely just completed. */
    document.fonts?.addEventListener?.("loadingdone", () => {
      try {
        if (document.fonts.check("600 16px 'Manrope Variable'", "CreateExtendSecure")) {
          confirmManrope();
        }
      } catch {
        /* keep cushion */
      }
    });

    let switchTimer: ReturnType<typeof setInterval> | null = null;

    const tick = () => {
      wordIdx = (wordIdx + 1) % switchWords.length;
      slotIdx -= 1;
      sw.setAttribute("aria-label", switchWords[wordIdx]);

      if (prefersReducedMotion.matches) {
        setTrackPosition(false);
      } else {
        setTrackPosition(true);
      }

      if (slotIdx === 0) {
        // Reached the leading Build; after the slide, snap back to the trailing
        // Build duplicate (last slot) for a seamless loop.
        window.setTimeout(
          () => {
            slotIdx = downwardSwitchWords.length - 1;
            setTrackPosition(false);
          },
          prefersReducedMotion.matches ? 0 : switchTransitionMs
        );
      }
    };

    const startRotation = () => {
      if (switchTimer === null) switchTimer = setInterval(tick, switchStepMs);
    };

    /* Reset to the initial "Build" slot. Safari restores a page from the
       bfcache (back/forward, and some reloads) frozen exactly as it was left -
       mid-rotation - so without this the word reappears stuck on whatever slot
       it paused at (typically a mis-positioned "Scale") instead of the intended
       "Build" start state. This is the "works once, then shows a crooked Scale"
       symptom. Re-run layout too, in case the font state changed while away. */
    const resetRotation = () => {
      if (switchTimer !== null) {
        clearInterval(switchTimer);
        switchTimer = null;
      }
      wordIdx = 0;
      slotIdx = downwardSwitchWords.length - 1;
      applySwitchLayout();
      startRotation();
    };

    startRotation();

    window.addEventListener("pageshow", (e: PageTransitionEvent) => {
      if (e.persisted) resetRotation();
    });
  }
}

window.addEventListener(
  "scroll",
  () => {
    if (header) {
      header.classList.toggle("scrolled", window.scrollY > 6);
    }
  },
  { passive: true }
);

{
  const track = document.querySelector(
    ".dex-figma-services__track"
  ) as HTMLElement | null;
  if (track) {
    let drag = false;
    let lastX = 0;
    let raf = 0;
    let pendingDx = 0;

    const flushScroll = () => {
      raf = 0;
      if (pendingDx === 0) return;
      track!.scrollLeft -= pendingDx;
      pendingDx = 0;
    };

    // Prevent native image dragging (ghost image) inside service cards.
    track.querySelectorAll("img").forEach((img) => {
      img.setAttribute("draggable", "false");
    });
    track.addEventListener("dragstart", (e) => {
      const t = e.target as Element | null;
      if (t?.closest?.("img")) e.preventDefault();
    });

    track.addEventListener("mousedown", (e: MouseEvent) => {
      if (e.button !== 0) return;
      const t = e.target as Element | null;
      if (t?.closest?.(".dex-svc-gd-tabs--bars")) return;
      drag = true;
      lastX = e.clientX;
      pendingDx = 0;
      if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
      track!.classList.add("is-dragging");
    });

    const stopDrag = () => {
      if (!drag) return;
      drag = false;
      if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
      if (pendingDx !== 0) {
        track!.scrollLeft -= pendingDx;
        pendingDx = 0;
      }
      track!.classList.remove("is-dragging");
    };

    document.addEventListener("mouseup", stopDrag);
    window.addEventListener("blur", stopDrag);
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) stopDrag();
    });

    document.addEventListener("mousemove", (e: MouseEvent) => {
      if (!drag) return;
      e.preventDefault();
      const x = e.clientX;
      pendingDx += x - lastX;
      lastX = x;
      if (!raf) raf = requestAnimationFrame(flushScroll);
    });
  }
}

{
  const tablist = document.querySelector(
    ".dex-svc-gd-tabs--bars"
  ) as HTMLElement | null;
  if (tablist) {
    const card = tablist.closest(".dex-figma-svc-card--white");
    const panes = card
      ? Array.from(card.querySelectorAll(".dex-svc-gd-pane"))
      : [];
    const bars = Array.from(
      tablist.querySelectorAll(".dex-svc-gd-bar")
    ) as HTMLElement[];
    const n = Math.max(panes.length, bars.length, 1);
    const mqReduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    let timer: ReturnType<typeof setInterval> | null = null;
    let onFillEnd: (() => void) | null = null;

    const clearFillEnd = () => {
      if (onFillEnd) {
        bars.forEach((b) => {
          const f = b.querySelector(".dex-svc-gd-bar__fill");
          if (f) f.removeEventListener("animationend", onFillEnd!);
        });
        onFillEnd = null;
      }
    };

    const resetFills = () => {
      bars.forEach((b) => {
        const f = b.querySelector(".dex-svc-gd-bar__fill") as HTMLElement | null;
        if (!f) return;
        f.classList.remove("svc-gd-bar__fill--playing");
        if (mqReduce.matches) {
          f.style.removeProperty("transform");
          f.style.removeProperty("animation");
        } else {
          f.style.transform = "scaleX(0)";
          f.style.animation = "none";
          void f.offsetWidth;
          f.style.removeProperty("animation");
        }
      });
    };

    const goTo = (i: number) => {
      const idx = ((i % n) + n) % n;
      bars.forEach((b, j) => {
        const on = j === idx;
        b.classList.toggle("dex-is-active", on);
        b.setAttribute("aria-selected", on ? "true" : "false");
      });
      panes.forEach((img, j) => img.classList.toggle("dex-is-active", j === idx));
    };

    const startProgress = () => {
      clearFillEnd();
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      resetFills();
      if (mqReduce.matches) {
        timer = setInterval(() => {
          const cur = bars.findIndex((b) => b.classList.contains("dex-is-active"));
          goTo(cur >= 0 ? (cur + 1) % n : 0);
        }, 5000);
        return;
      }
      const idx = bars.findIndex((b) => b.classList.contains("dex-is-active"));
      const fill = bars[idx]
        ? (bars[idx].querySelector(".dex-svc-gd-bar__fill") as HTMLElement | null)
        : null;
      if (!fill) return;
      onFillEnd = () => {
        clearFillEnd();
        goTo((idx + 1) % n);
        startProgress();
      };
      fill.addEventListener("animationend", onFillEnd);
      requestAnimationFrame(() => {
        fill.style.removeProperty("transform");
        fill.classList.add("svc-gd-bar__fill--playing");
      });
    };

    bars.forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = Number((btn as HTMLElement).dataset.gd);
        if (Number.isNaN(id)) return;
        goTo(id);
        startProgress();
      });
    });

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        clearFillEnd();
        resetFills();
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      } else {
        startProgress();
      }
    });

    startProgress();
  }
}

{
  const tablist = document.querySelector(
    ".dex-why-steps-tabs"
  ) as HTMLElement | null;
  if (tablist) {
    const tabs = Array.from(
      tablist.querySelectorAll(".dex-why-step-tab")
    ) as HTMLElement[];
    const panels = Array.from(
      document.querySelectorAll(".dex-why-step-panel")
    ) as HTMLElement[];
    const n = Math.max(tabs.length, panels.length, 1);
    const mqReduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const stepMs = 5000;
    let timer: ReturnType<typeof setInterval> | null = null;
    let stepTimeout: ReturnType<typeof setTimeout> | null = null;

    const clearStepDelay = () => {
      if (stepTimeout) {
        clearTimeout(stepTimeout);
        stepTimeout = null;
      }
    };

    const goTo = (i: number) => {
      const idx = ((i % n) + n) % n;
      tabs.forEach((tab, j) => {
        const on = j === idx;
        tab.classList.toggle("dex-is-active", on);
        tab.setAttribute("aria-selected", on ? "true" : "false");
        tab.setAttribute("tabindex", on ? "0" : "-1");
      });
      panels.forEach((p, j) => {
        const show = j === idx;
        p.classList.toggle("dex-is-active", show);
        p.setAttribute("aria-hidden", show ? "false" : "true");
      });
    };

    const startProgress = () => {
      clearStepDelay();
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      if (mqReduce.matches) {
        timer = setInterval(() => {
          const cur = tabs.findIndex((t) => t.classList.contains("dex-is-active"));
          goTo(cur >= 0 ? (cur + 1) % n : 0);
        }, stepMs);
        return;
      }
      const idx = tabs.findIndex((t) => t.classList.contains("dex-is-active"));
      if (idx < 0) return;
      stepTimeout = setTimeout(() => {
        stepTimeout = null;
        goTo((idx + 1) % n);
        startProgress();
      }, stepMs);
    };

    tabs.forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = Number((btn as HTMLElement).dataset.whyStep);
        if (Number.isNaN(id)) return;
        goTo(id);
        startProgress();
      });
    });

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        clearStepDelay();
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      } else {
        startProgress();
      }
    });

    panels.forEach((p, j) =>
      p.setAttribute("aria-hidden", j === 0 ? "false" : "true")
    );
    startProgress();
  }
}

/* Schedule modal form handles its own submit (validation + success); do not intercept here */
document.querySelectorAll(".dex-blocks form, form.hero-form").forEach((form) => {
  form.addEventListener("submit", (e) => {
    e.preventDefault();
  });
});

{
  const container = document.getElementById("cases-swiper");
  const prevBtn = document.getElementById("cases-prev");
  const nextBtn = document.getElementById("cases-next");
  if (container) {
    const scrollY = window.scrollY;
    const swiper = new Swiper(container, {
      modules: [Navigation, Mousewheel],
      slidesPerView: 1.15,
      centeredSlides: true,
      loop: true,
      speed: 500,
      spaceBetween: 12,
      grabCursor: true,
      a11y: { enabled: false },
      mousewheel: {
        forceToAxis: true,
        sensitivity: 1,
        thresholdDelta: 6,
      },
      navigation: {
        prevEl: prevBtn,
        nextEl: nextBtn,
      },
      breakpoints: {
        640: { slidesPerView: 1.5 },
        1024: { slidesPerView: 2.3 },
      },
    });
    container.querySelectorAll(".swiper-slide").forEach((s) => {
      s.setAttribute("tabindex", "-1");
    });
    window.scrollTo(0, scrollY);
    requestAnimationFrame(() => window.scrollTo(0, scrollY));
  }
}

{
  const masonry = document.getElementById("reviews-masonry");
  const readBtn = document.getElementById("reviews-readmore") as HTMLButtonElement | null;
  const readLessBtn = document.getElementById("reviews-readless") as
    | HTMLButtonElement
    | null;
  if (masonry && readBtn && readLessBtn) {
    const panel = masonry.closest(".dex-reviews-masonry-panel");

    const setExpanded = (expanded: boolean) => {
      masonry.classList.toggle("dex-reviews-masonry--expanded", expanded);
      if (panel) panel.classList.toggle("dex-reviews-masonry-panel--expanded", expanded);
      readBtn.setAttribute("aria-expanded", expanded ? "true" : "false");
      readLessBtn.setAttribute("aria-expanded", expanded ? "true" : "false");
      const readMoreWrap = readBtn.closest(".dex-reviews-readmore-wrap") as HTMLElement | null;
      if (readMoreWrap) readMoreWrap.hidden = false;
    };

    readBtn.addEventListener("click", () => {
      setExpanded(true);
    });

    readLessBtn.addEventListener("click", () => {
      setExpanded(false);
    });
  }
}
